import React, { useState, useRef, useEffect } from 'react';
import './SearchableDropdown.css';

const SearchableDropdown = ({
  options,
  value,
  onChange,
  placeholder = "Seleccionar...",
  emptyLabel = "Todos",
  label,
  id,
  // Modo multiple: `value` es un array y `onChange` recibe un array.
  // El menu queda abierto al elegir, para poder marcar varias sin reabrir.
  multiple = false,
  itemNoun = "selected"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const selected = multiple ? (Array.isArray(value) ? value : []) : value;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    if (multiple) {
      // '' es la fila "todos": limpia la seleccion en vez de agregarse como valor
      if (option === '') {
        onChange([]);
        return;
      }
      onChange(
        selected.includes(option)
          ? selected.filter(v => v !== option)
          : [...selected, option]
      );
      return;
    }
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const isSelected = (option) =>
    multiple ? selected.includes(option) : value === option;

  let displayValue;
  if (multiple) {
    if (selected.length === 0) displayValue = emptyLabel;
    else if (selected.length === 1) displayValue = selected[0];
    else displayValue = `${selected.length} ${itemNoun}`;
  } else {
    displayValue = value || emptyLabel;
  }

  return (
    <div className="searchable-dropdown" ref={dropdownRef}>
      {label && <label htmlFor={id}>{label}</label>}
      <div className="dropdown-container">
        <button
          type="button"
          className="dropdown-toggle"
          onClick={() => setIsOpen(!isOpen)}
          id={id}
        >
          <span className="dropdown-value">{displayValue}</span>
          <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
        </button>

        {isOpen && (
          <div className="dropdown-menu">
            <div className="dropdown-search">
              <input
                type="text"
                className="search-input"
                placeholder={`🔍 ${placeholder}`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>

            <div className="dropdown-options">
              <div
                className={`dropdown-option ${(multiple ? selected.length === 0 : !value) ? 'selected' : ''}`}
                onClick={() => handleSelect('')}
              >
                {emptyLabel}
              </div>

              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <div
                    key={index}
                    className={`dropdown-option${multiple ? ' dropdown-option--multi' : ''} ${isSelected(option) ? 'selected' : ''}`}
                    onClick={() => handleSelect(option)}
                  >
                    {multiple && (
                      <input
                        type="checkbox"
                        className="dropdown-check"
                        checked={isSelected(option)}
                        readOnly
                        tabIndex={-1}
                      />
                    )}
                    {option}
                  </div>
                ))
              ) : (
                <div className="dropdown-no-results">
                  No se encontraron resultados
                </div>
              )}
            </div>

            {multiple && (
              <div className="dropdown-actions">
                <button
                  type="button"
                  className="dropdown-action"
                  onClick={() => {
                    // agrega lo que hay visible en la busqueda, sin perder lo ya marcado
                    const merged = new Set(selected);
                    filteredOptions.forEach(o => merged.add(o));
                    onChange(Array.from(merged));
                  }}
                  disabled={filteredOptions.length === 0}
                >
                  Select all{searchTerm ? ' shown' : ''}
                </button>
                <button
                  type="button"
                  className="dropdown-action dropdown-action--muted"
                  onClick={() => onChange([])}
                  disabled={selected.length === 0}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableDropdown;
